import { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, CheckCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { purchasingAPI, ReasonCode } from '../../lib/api';

/** Data shape for GRN workflow actions (acknowledge, dispute, resolve). */
export interface GRNWorkflowData {
  grnId: number;
  action: 'acknowledge' | 'dispute' | 'resolve';
  comments?: string;
  reason_id?: number;
}

interface GRNWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  grnId: number;
  grnNumber: string;
  partNumber: string;
  poNumber: string;
  onSubmit: (data: GRNWorkflowData) => Promise<void>;
}

// Acknowledge GRN Modal (Supplier acknowledges receipt)
export function AcknowledgeGRNModal({
  isOpen,
  onClose,
  grnId,
  grnNumber,
  partNumber,
  poNumber,
  onSubmit,
}: GRNWorkflowModalProps) {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        grnId,
        action: 'acknowledge',
        comments,
      });
      setComments('');
      onClose();
    } catch (error) {
      console.error('Failed to acknowledge GRN:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold">Acknowledge GRN</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">GRN:</span>
                <span className="font-mono">{grnNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">PO:</span>
                <span className="font-mono">{poNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                By acknowledging this GRN, you confirm that the goods receipt details are accurate.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Add any comments regarding this acknowledgment..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {submitting ? 'Acknowledging...' : 'Acknowledge GRN'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Dispute GRN Modal (Supplier disputes the receipt)
export function DisputeGRNModal({
  isOpen,
  onClose,
  grnId,
  grnNumber,
  partNumber,
  poNumber,
  onSubmit,
}: GRNWorkflowModalProps) {
  const [reasonCodeId, setReasonCodeId] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchReasonCodes();
    }
  }, [isOpen]);

  const fetchReasonCodes = async () => {
    try {
      setLoadingReasons(true);
      const response = await purchasingAPI.getReasonCodes({ is_active: true });
      setReasonCodes(response.data.results);
    } catch (error) {
      console.error('Failed to fetch reason codes:', error);
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasonCodeId) {
      alert('Please select a reason for the dispute');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        grnId,
        action: 'dispute',
        reason_id: parseInt(reasonCodeId),
        comments,
      });
      setReasonCodeId('');
      setComments('');
      onClose();
    } catch (error) {
      console.error('Failed to dispute GRN:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Dispute GRN</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">GRN:</span>
                <span className="font-mono">{grnNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">PO:</span>
                <span className="font-mono">{poNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                Raise a dispute if you believe there are issues with the goods receipt details or quantities.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Reason for Dispute <span className="text-red-500">*</span>
              </label>
              {loadingReasons ? (
                <div className="text-sm text-muted-foreground">Loading reasons...</div>
              ) : (
                <select
                  value={reasonCodeId}
                  onChange={(e) => setReasonCodeId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a reason...</option>
                  {reasonCodes.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.code} - {reason.description}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide details about the dispute..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !reasonCodeId}
                className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                {submitting ? 'Raising Dispute...' : 'Raise Dispute'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Resolve GRN Dispute Modal (Customer resolves the dispute)
export function ResolveGRNDisputeModal({
  isOpen,
  onClose,
  grnId,
  grnNumber,
  partNumber,
  poNumber,
  onSubmit,
}: GRNWorkflowModalProps) {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        grnId,
        action: 'resolve',
        comments,
      });
      setComments('');
      onClose();
    } catch (error) {
      console.error('Failed to resolve GRN dispute:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <CheckCheck className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Resolve GRN Dispute</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">GRN:</span>
                <span className="font-mono">{grnNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">PO:</span>
                <span className="font-mono">{poNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                By resolving this dispute, you confirm that the issue has been addressed and the GRN is accepted.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Resolution Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Describe how the dispute was resolved..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCheck className="h-4 w-4" />
                {submitting ? 'Resolving...' : 'Resolve Dispute'}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
